import React, { useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { ContainerInfo, ContainerInfoForm } from '@components/obsolete/ContainerInfo/ContainerInfo.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { ValidationError } from 'yup';
import { hasValidationProblemForPrefix } from '@utils/validation.tsx';
import SvgContainer from '@icons/Container.tsx';

interface ComponentProps {
  secured?: SecuredConfig;
  value: ContainerInfoForm;
  onChange: (value: ContainerInfoForm) => void;
  readOnly?: boolean;
  required?: boolean;
  validationErrors?: ValidationError[];
  dataTestId?: string;
}

const ContainerTag: React.FC<ComponentProps> = ({ secured, value, onChange, readOnly, required = false, validationErrors, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const [internalValue, setInternalValue] = useState<ContainerInfoForm>(value);

  useEffect(() => setInternalValue(value), [value]);

  const printValue = (v: ContainerInfoForm) => {
    if (isNullOrUndefined(v) || (isNullOrUndefined(v.containerInfoCode) && isNullOrUndefined(v.containerId))) return null;
    return [v.containerInfoCode, v.containerId].filter(t => t != null && t !== '').join(', ');
  };

  const hasError = validationErrors ? hasValidationProblemForPrefix(validationErrors, 'containerInfo') : false;

  return (
    <ButtonTag
      secured={secured}
      label={tr('ContainerTag.label', 'Container')}
      icon={<SvgContainer />}
      value={printValue(value)}
      readonly={readOnly}
      required={required}
      errorMessage={hasError ? tr('ContainerTag.validationError', 'Validation error') : undefined}
      onSave={onSuccess => {
        onChange(internalValue);
        onSuccess();
      }}
      popupWidth={'400px'}
      autoClose
      dataTestId={dataTestId}
    >
      <ContainerInfo
        containerInfo={internalValue}
        onChange={v => setInternalValue(v)}
        clearable={true}
        onClear={() => setInternalValue(null)}
        inSidebar={true}
        validationErrors={validationErrors}
        disabled={readOnly}
      />
    </ButtonTag>
  );
};

export default ContainerTag;
